import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import axiosInstance from '../utils/axios-auth';
import { getUserFacingMessageFromAxiosError } from '../utils/user-facing-api-error';
// Helper to validate engagement level values
export const isValidEngagementLevel = (level) => {
    return ['Engaged', 'Strategic_Compliance', 'Ritual_Compliance', 'Retreatism', 'Rebellion'].includes(level);
};
// Async thunk for fetching all engagement levels
export const fetchEngagementLevels = createAsyncThunk('engagement/fetchEngagementLevels', async (sessionId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/appointments/${sessionId}/engagement_levels`);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch engagement levels' }));
    }
});
// Async thunk for fetching a single engagement level
export const fetchEngagementLevelById = createAsyncThunk('engagement/fetchEngagementLevelById', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/engagement_levels/${id}`);
        return response.data.data;
    }
    catch (error) {
        if (isAxiosError(error)) {
            return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch engagement level' }));
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
// Async thunk for creating a new engagement level
export const createEngagementLevel = createAsyncThunk('engagement/createEngagementLevel', async (engagementData, { rejectWithValue }) => {
    try {
        // Validate that level is a valid ENUM value
        if (!isValidEngagementLevel(engagementData.level)) {
            return rejectWithValue(`Invalid engagement level: ${engagementData.level}`);
        }
        const response = await axiosInstance.post('/engagement_levels', engagementData);
        return response.data.data;
    }
    catch (error) {
        if (isAxiosError(error)) {
            return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to create engagement level' }));
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
// Async thunk for updating an engagement level
export const updateEngagementLevel = createAsyncThunk('engagement/updateEngagementLevel', async ({ id, updateData }, { rejectWithValue }) => {
    try {
        // Validate level if it's being updated
        if (updateData.level && !isValidEngagementLevel(updateData.level)) {
            return rejectWithValue(`Invalid engagement level: ${updateData.level}`);
        }
        const response = await axiosInstance.put(`/engagement_levels/${id}`, updateData);
        return response.data.data;
    }
    catch (error) {
        if (isAxiosError(error)) {
            return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update engagement level' }));
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
// Async thunk for deleting an engagement level
export const deleteEngagementLevel = createAsyncThunk('engagement/deleteEngagementLevel', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/engagement_levels/${id}`);
        return id; // Return the id for filtering in the reducer
    }
    catch (error) {
        if (isAxiosError(error)) {
            return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to delete engagement level' }));
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
// Get engagement levels for a specific student
export const fetchStudentEngagementLevels = createAsyncThunk('engagement/fetchStudentEngagementLevels', async (studentId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/students/${studentId}/engagement_levels`);
        return response.data.data;
    }
    catch (error) {
        if (isAxiosError(error)) {
            return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch student engagement levels' }));
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
// Get engagement levels for a specific session
export const fetchSessionEngagementLevels = createAsyncThunk('engagement/fetchSessionEngagementLevels', async (sessionId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/sessions/${sessionId}/engagement_levels`);
        return response.data.data;
    }
    catch (error) {
        if (isAxiosError(error)) {
            return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch session engagement levels' }));
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
const engagementSlice = createSlice({
    name: 'engagement',
    initialState: {
        data: [],
        current_engagement_level: null,
        status: 'idle',
        error: null,
    },
    reducers: {
        clearEngagementError: (state) => {
            state.error = null;
        },
        setCurrentEngagementLevel: (state, action) => {
            state.current_engagement_level = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchEngagementLevels
            .addCase(fetchEngagementLevels.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetchEngagementLevels.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data = action.payload;
            state.error = null;
        })
            .addCase(fetchEngagementLevels.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Handle fetchEngagementLevelById
            .addCase(fetchEngagementLevelById.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetchEngagementLevelById.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_engagement_level = action.payload;
            state.error = null;
        })
            .addCase(fetchEngagementLevelById.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Handle createEngagementLevel
            .addCase(createEngagementLevel.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(createEngagementLevel.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data.push(action.payload);
            state.current_engagement_level = action.payload;
            state.error = null;
        })
            .addCase(createEngagementLevel.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Handle updateEngagementLevel
            .addCase(updateEngagementLevel.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(updateEngagementLevel.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const index = state.data.findIndex(item => item.id === action.payload.id);
            if (index !== -1) {
                state.data[index] = action.payload;
            }
            state.current_engagement_level = action.payload;
            state.error = null;
        })
            .addCase(updateEngagementLevel.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Handle deleteEngagementLevel
            .addCase(deleteEngagementLevel.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(deleteEngagementLevel.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data = state.data.filter(item => item.id !== action.payload);
            if (state.current_engagement_level?.id === action.payload) {
                state.current_engagement_level = null;
            }
            state.error = null;
        })
            .addCase(deleteEngagementLevel.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Handle fetchStudentEngagementLevels
            .addCase(fetchStudentEngagementLevels.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data = action.payload;
            state.error = null;
        })
            // Handle fetchSessionEngagementLevels
            .addCase(fetchSessionEngagementLevels.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.data = action.payload;
            state.error = null;
        });
    },
});
export const { clearEngagementError, setCurrentEngagementLevel } = engagementSlice.actions;
export default engagementSlice.reducer;
